import type { GgxTourDefinition } from './tour-types';

export const START_01: GgxTourDefinition = {
    key: '[START-01] Know your way around',
    route: '/home',
    required: true,
    estimatedMinutes: 2,
    intro: {
        headline: "Welcome, {name}. Let's get you set up.",
        subtitle:
            'Two minutes, three chapters. You will know where your work lives, how to watch ' +
            'it run, and where to find help when you need it.',
    },
    outro: {
        title: "You're set up.",
        subtitle:
            'You know where your work lives, how to watch it run, and where to find help. ' +
            'The Learning Hub keeps this tour if you ever want it again.',
        learned: [
            '**Four modules** on the home page hold everything you build.',
            '**Running jobs** report their progress on the home page.',
            '**Search and the Learning Hub** are there whenever you are stuck.',
        ],
    },
    chapters: [
        {
            name: 'Your workspace',
            blurb: 'The four modules and your recent work',
            icon: 'map',
            steps: [2, 3],
        },
        {
            name: 'Track your work',
            blurb: 'Where running jobs report progress',
            icon: 'pulse',
            steps: [4],
        },
        {
            name: 'Find your way',
            blurb: 'Search, and the Learning Hub',
            icon: 'compass',
            steps: [5, 6],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-cards"]',
            title: "Let's start with your home page",
            whatHappens: 'clicking Launch on a card takes you into that module.',
            icon: 'grid',
            description:
                'Everything you do in GGX falls into one of four stages, and each card here is ' +
                'one of them. <strong>Data & AI Assets</strong> is where your models, prompts ' +
                'and pipelines live. <strong>Human Integrated Testing</strong> is where people ' +
                'try them out. <strong>Risk & Compliance</strong> is where you document and ' +
                'review risk, and <strong>Monitor & Track</strong> watches everything once it is ' +
                'running. You can launch any of them from here.',
            side: 'bottom',
            align: 'center',
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="recent-activity"]',
            title: 'Pick up where you left off',
            whatHappens: 'clicking any item here opens it straight away.',
            icon: 'clock',
            description:
                'This panel remembers what you opened recently: pipelines, prompts, datasets, ' +
                'reports. Instead of hunting through the modules, come back here and click the ' +
                'item. Most of the how-to tours start from this panel too.',
            side: 'right',
            align: 'start',
            actionLabel: 'an item',
            draftCopy: true,
        },
        {
            target: '[test-id="recent-jobs"]',
            title: 'Keep an eye on what is running',
            whatHappens: 'each job here links to its results once it finishes.',
            icon: 'list',
            description:
                'Some work in GGX takes a while, like an evaluation that sends hundreds of ' +
                'questions through a pipeline. That work runs in the background as a job, and ' +
                'this panel shows you what is still running and what has finished, so you never ' +
                'have to wonder.',
            side: 'left',
            align: 'start',
            actionLabel: 'Running Jobs',
            draftCopy: true,
        },
        {
            target: '[test-id="search-button"]',
            title: 'Need something fast? Search for it',
            whatHappens: 'the search box opens, and results appear as you type.',
            icon: 'search',
            description:
                'Quick Search finds any object in GGX by name: a pipeline, a prompt, a dataset, ' +
                'a report. It saves you from clicking through the modules when you already know ' +
                'what you want. You can also press <b>Ctrl+K</b> from anywhere to open it.',
            side: 'bottom',
            align: 'end',
            actionLabel: 'Search',
            draftCopy: true,
        },
        {
            target: '[test-id="learning-hub-btn"]',
            title: 'Help is always one click away',
            whatHappens: 'the Learning Hub opens with the tours you can take next.',
            icon: 'cap',
            description:
                'The Learning Hub holds every guided tour, including this one, in three tracks: ' +
                'Getting Started, How-To Guides and Learn AI. Whenever you want to learn a new ' +
                'part of GGX or refresh your memory, this is the place to come.',
            side: 'bottom',
            align: 'end',
            actionLabel: 'Visit the Learning Hub',
            draftCopy: true,
        },
    ],
};
